import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  resolveSeo,
  getSiteUrl,
  SITE_NAME,
  SITE_IMAGE_URL,
} from '@/lib/seoConfig';

// Récupère une balise <meta> existante (par name ou property) ou la crée.
// Garantit qu'il n'existe qu'une seule balise gérée par nom/propriété.
function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function upsertCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

// Gestionnaire centralisé des métadonnées SEO.
// À chaque changement de route, il réécrit l'intégralité des balises gérées
// (titre, description, mots-clés, canonical, Open Graph, Twitter Card, robots)
// pour qu'aucune valeur de la route précédente ne « fuite » sur la suivante.
export default function RouteSeo() {
  const location = useLocation();

  useEffect(() => {
    const siteUrl = getSiteUrl();
    const seo = resolveSeo(location.pathname);
    const canonical =
      seo.canonicalPath === '/' ? `${siteUrl}/` : `${siteUrl}${seo.canonicalPath}`;

    // Balises principales
    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    if (seo.keywords) {
      upsertMeta('name', 'keywords', seo.keywords);
    } else {
      removeMeta('name', 'keywords');
    }

    // Canonical
    upsertCanonical(canonical);

    // Open Graph
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:locale', 'fr_MA');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:image', SITE_IMAGE_URL);

    // Twitter Card
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', SITE_IMAGE_URL);

    // Robots : la 404 et les routes inconnues ne doivent pas être indexées.
    upsertMeta('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow');
  }, [location.pathname]);

  return null;
}